// components/auth/RoleBootstrap.js

import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { ensureAuthenticatedSessionBootstrapped } from "../../services/authService";

import { getActiveOrganizationSelection } from "../shared/util";
import { hasPendingOrgSwitchNavigation } from "../shared/utils/authSessionState";

import {
  ACTIVE_ORGANIZATION_ROLE_REFRESH_EVENT,
  clearLoggedInUserRole,
  loadLoggedInUserRole,
} from "../shared/access";

const getStorageItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const getCurrentTenantId = () => {
  const activeOrganizationSelection = getActiveOrganizationSelection();

  return (
    activeOrganizationSelection?.tenantId ||
    getStorageItem("mainTenantId") ||
    getStorageItem("tenantId")
  );
};

function RoleBootstrap({ children }) {
  const { isAuthenticated, loading, login } = useAuth();
  const [roleLoading, setRoleLoading] = useState(true);
  const loginRef = useRef(login);

  useEffect(() => {
    loginRef.current = login;
  }, [login]);

  useEffect(() => {
    let isMounted = true;

    const loadRole = async ({ force = false } = {}) => {
      if (loading) {
        return;
      }

      if (!isAuthenticated) {
        clearLoggedInUserRole();

        if (isMounted) {
          setRoleLoading(false);
        }

        return;
      }

      const tenantId = getCurrentTenantId();

      if (!tenantId) {
        clearLoggedInUserRole();

        if (isMounted) {
          setRoleLoading(false);
        }

        return;
      }

      if (isMounted) {
        setRoleLoading(true);
      }

      try {
        if (!hasPendingOrgSwitchNavigation()) {
          const bootstrapResponse =
            await ensureAuthenticatedSessionBootstrapped();

          if (!isMounted) {
            return;
          }

          if (bootstrapResponse) {
            loginRef.current(bootstrapResponse, {
              resetRefreshClock: false,
            });
          }
        }

        await loadLoggedInUserRole(tenantId, { force });
      } catch (error) {
        console.error("Failed to bootstrap authenticated role:", error);
        clearLoggedInUserRole();
      }

      if (isMounted) {
        setRoleLoading(false);
      }
    };

    void loadRole();

    const handleRoleRefresh = () => {
      void loadRole({ force: true });
    };

    window.addEventListener(
      ACTIVE_ORGANIZATION_ROLE_REFRESH_EVENT,
      handleRoleRefresh,
    );

    return () => {
      isMounted = false;
      window.removeEventListener(
        ACTIVE_ORGANIZATION_ROLE_REFRESH_EVENT,
        handleRoleRefresh,
      );
    };
  }, [isAuthenticated, loading]);

  if (loading || roleLoading) {
    return null;
  }

  return children;
}

export default RoleBootstrap;
